import { computed, onScopeDispose, ref, shallowRef, watch, type Ref } from 'vue'
import { execute, TransportError } from '@/graphql/client'
// The operation itself lives in `ScanReport.graphql` beside this file, which is
// what lets codegen, a schema-diff job and a linter tell the operations apart
// from the contract they run against.
import { ScanReportDocument, type ScanReportQuery } from '@/graphql/generated/graphql'

export type Scan = NonNullable<ScanReportQuery['scan']>
export type Finding = Scan['findings'][number]
export type ScanErrorEntry = Scan['errors'][number]

/** Nothing is known yet, something is known, or the id names no scan. */
export type LoadPhase = 'loading' | 'ready' | 'missing' | 'error'

const POLL_INTERVAL_MS = 1000
const BACKOFF_BASE_MS = 1000
const BACKOFF_MAX_MS = 8000
const BACKOFF_STEPS = 3
const FAILURES_BEFORE_SURFACING = 4

/**
 * Polls one scan until it stops changing.
 *
 * The contract asks for roughly 1Hz while `state` is QUEUED or RUNNING, and for
 * the findings to be rendered as they land rather than at the end. A few
 * details around that are worth stating because they are the difference between
 * a loop that works and one that misbehaves in the cases people actually hit.
 *
 * It is a `setTimeout` chain rather than `setInterval`. The next poll is
 * scheduled after the previous one settles, so a slow response can never stack
 * requests on top of each other, which is exactly the moment a struggling
 * server would least like that to happen.
 *
 * It stops on FINISHED, and it stops on a null scan. A null means the id has
 * expired or never existed, and the contract calls that normal rather than
 * exceptional, so it is a rendered state and not an error.
 *
 * It pauses while the tab is hidden and polls immediately on the way back.
 * Thirty seconds of background polling for a page nobody is looking at is load
 * the operator pays for and the reader never sees, and the instant poll on
 * return means switching back to the tab shows fresh data rather than a
 * second-old snapshot that then jumps.
 *
 * Transient failures back off and stay quiet. One dropped request during a scan
 * is a blip, and replacing a half-drawn report with an error banner because of
 * it would be worse than saying nothing. The banner appears after four
 * consecutive failures, and the findings already on screen stay there because
 * they are still true.
 */
export function usePolledScan(id: Ref<string>) {
  const scan = shallowRef<Scan | null>(null)
  const phase = ref<LoadPhase>('loading')
  const error = ref<TransportError | null>(null)
  const consecutiveFailures = ref(0)
  const isPolling = ref(false)
  /** Set once the scan reaches FINISHED, so the UI can settle. */
  const isSettled = computed(() => scan.value !== null && scan.value.state === 'FINISHED')

  let timer: ReturnType<typeof setTimeout> | undefined
  let controller: AbortController | undefined
  let disposed = false

  function clearTimer() {
    if (timer !== undefined) {
      clearTimeout(timer)
      timer = undefined
    }
  }

  function schedule(delayMs: number) {
    clearTimer()
    if (disposed) return
    timer = setTimeout(() => {
      void tick()
    }, delayMs)
  }

  /**
   * The wait before the next attempt after a failure: 1s, 2s, 4s, then 8s for
   * as long as it keeps failing.
   *
   * Doubling rather than indexing a table, so there is no bounds check that can
   * never fail and no fallback branch no test can reach. Only ever called from
   * the catch block, which has already counted this failure.
   */
  function nextDelay(): number {
    const step = Math.min(consecutiveFailures.value - 1, BACKOFF_STEPS)
    return Math.min(BACKOFF_BASE_MS * 2 ** step, BACKOFF_MAX_MS)
  }

  async function tick() {
    // No `disposed` check here. `onScopeDispose` clears the pending timer, and
    // a timer that has been cleared does not fire, so there is no path into
    // this function after disposal. The check that matters is the one in the
    // catch block, where a request can still be in flight when the scope goes.

    // A hidden tab is not watching. Come back when it is.
    if (typeof document !== 'undefined' && document.hidden) {
      isPolling.value = false
      return
    }

    controller?.abort()
    controller = new AbortController()
    isPolling.value = true

    try {
      const data = await execute(ScanReportDocument, { id: id.value }, { signal: controller.signal })
      if (disposed) return

      consecutiveFailures.value = 0
      error.value = null

      if (data.scan === null || data.scan === undefined) {
        scan.value = null
        phase.value = 'missing'
        isPolling.value = false
        clearTimer()
        return
      }

      scan.value = data.scan
      phase.value = 'ready'

      if (data.scan.state === 'FINISHED') {
        isPolling.value = false
        clearTimer()
        return
      }

      schedule(POLL_INTERVAL_MS)
    } catch (cause) {
      if (disposed) return
      if (cause instanceof DOMException && cause.name === 'AbortError') return

      consecutiveFailures.value += 1
      const failure = cause instanceof TransportError ? cause : new TransportError('network', 'unknown')

      // Only surface a failure once it has happened enough times to be real. A
      // report already on screen is not wrong just because one poll dropped.
      if (consecutiveFailures.value >= FAILURES_BEFORE_SURFACING) {
        error.value = failure
        if (scan.value === null) phase.value = 'error'
      }

      schedule(nextDelay())
    } finally {
      if (!disposed) isPolling.value = false
    }
  }

  function onVisibilityChange() {
    // Same reasoning as `tick`: the listener is removed on dispose.
    if (document.hidden) {
      clearTimer()
      return
    }
    if (scan.value?.state === 'FINISHED' || phase.value === 'missing') return
    // Straight back to work, no interval wait. Returning to the tab should show
    // current data rather than a stale frame that updates a second later.
    schedule(0)
  }

  /** Manual retry from the error state. Clears the backoff. */
  function retry() {
    consecutiveFailures.value = 0
    error.value = null
    if (phase.value === 'error') phase.value = 'loading'
    schedule(0)
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange)
  }

  watch(
    id,
    () => {
      scan.value = null
      phase.value = 'loading'
      error.value = null
      consecutiveFailures.value = 0
      schedule(0)
    },
    { immediate: true },
  )

  onScopeDispose(() => {
    disposed = true
    clearTimer()
    controller?.abort()
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  })

  return { scan, phase, error, isPolling, isSettled, consecutiveFailures, retry }
}
